<template>
    <div v-if="visible" class="drawing-edit-popup dep-settings-dialog" role="dialog" aria-label="Drawing settings"
        :class="{ 'dep-dark': isDark }"
        :style="popupStyle"
        @mousedown.stop="startDrag">
        <div class="dep-header">
            <span class="dep-title">{{ toolName }} settings</span>
            <button class="dep-close" @click="close" title="Close">&times;</button>
        </div>
        <nav class="dep-settings-tabs" aria-label="Drawing settings sections"><button v-for="section in (/Auto\s*Pattern/i.test(toolName)?['Style','Patterns','Coordinates & visibility']:['Style','Coordinates & visibility'])" :key="section" :class="{active:settingsSection===section}" @click="settingsSection=section">{{section}}</button></nav>
        <div class="dep-body">
        <section v-if="/Auto\s*Pattern/i.test(toolName)" v-show="settingsSection==='Patterns'" aria-label="Pattern visibility">
            <p>{{ enabledPatternCount }} / {{ patternCatalogSize }} patterns enabled</p>
            <div class="dep-row"><input v-model="patternSearch" aria-label="Search patterns" placeholder="Search patterns" class="dep-text-input" @mousedown.stop /></div>
            <div class="dep-row"><button type="button" @click="selectPatterns(true)">Select all patterns</button><button type="button" @click="selectPatterns(false)">Clear all patterns</button></div>
            <div class="dep-row"><button type="button" @click="selectShownPatterns(true)">Enable shown patterns</button><button type="button" @click="selectShownPatterns(false)">Hide shown patterns</button></div>
            <div class="dep-pattern-list">
                <label v-for="pattern in patternChoices" :key="pattern.id" class="dep-pattern-choice" :title="pattern.kind+' · '+pattern.bias+' candidate'">
                    <input type="checkbox" :aria-label="'Show pattern '+pattern.name" :checked="settings.patternVisibility?.[pattern.id]!==false" @change="togglePattern(pattern.id,$event.target.checked)" />
                    <PatternSymbolIcon :symbol="pattern.symbol" :style="{color:pattern.bias==='bullish'?'#26a69a':pattern.bias==='bearish'?'#ef5350':'#a78bfa'}" />
                    <span>{{ pattern.name }}</span>
                </label>
            </div>
            <div class="dep-row"><label><input type="checkbox" :checked="settings.requirePatternTrend!==false" @change="change({requirePatternTrend:$event.target.checked})" /> Require prior trend for reversal candles</label></div>
            <div class="dep-row"><label>Swing confirmation bars<input aria-label="Pattern swing confirmation bars" type="number" min="2" max="10" :value="settings.patternPivotStrength || 2" @change="change({patternPivotStrength:Math.max(2,Math.min(10,Number($event.target.value)||2))})" /></label></div>
            <small>Symbols describe detected candidates. Formation outlines and labels can be adjusted in Style.</small>
        </section>
        <div v-show="settingsSection==='Style'">
            <!-- Text input for text/label/callout tools -->
            <div class="dep-row" v-if="isTextTool">
                <label class="dep-label">Text</label>
                <textarea class="dep-text-input" rows="3"
                    :value="currentText"
                    @input="setText($event.target.value)"
                    @mousedown.stop
                    placeholder="Enter text..."></textarea>
            </div>
            <div class="dep-row drawing-symbol-picker" v-if="/Icon|Sticker/.test(toolName)"><button v-for="icon in iconOptions" :key="icon.id" :title="icon.label" :aria-label="'Drawing icon '+icon.label" @click="change({iconId:icon.id,color:icon.color})"><svg viewBox="0 0 24 24" width="24" height="24" :fill="icon.color"><path :d="icon.path" fill-rule="evenodd" /></svg></button></div>
            <div class="dep-row drawing-symbol-picker" v-if="/Emoji/.test(toolName)"><button v-for="emoji in emojiOptions" :key="emoji" @click="change({text:emoji})">{{emoji}}</button></div>
            <!-- Color + width share one row; width/style only matter for tools that draw a line -->
            <div class="dep-row dep-style-row">
                <div class="dep-style-col dep-style-col-color">
                    <label class="dep-label">Color</label>
                    <div class="dep-colors">
                        <span v-for="c in palette" :key="c"
                            class="dep-swatch"
                            :class="{ active: c === currentColor }"
                            :style="swatchStyle(c)"
                            @click="setColor(c)">
                        </span>
                        <!-- Custom color input -->
                        <label class="dep-swatch dep-custom-color" title="Custom color">
                            <input type="color" :value="currentColor"
                                @input="setColor($event.target.value)"
                                @mousedown.stop
                                class="dep-color-input" />
                            <span class="dep-custom-icon">+</span>
                        </label>
                    </div>
                </div>
                <div class="dep-style-col" v-if="hasLine">
                    <label class="dep-label">Width</label>
                    <div class="dep-widths">
                        <span v-for="w in widths" :key="w"
                            class="dep-width-btn"
                            :class="{ active: w === currentWidth }"
                            @click="setWidth(w)">
                            <span class="dep-width-line" :style="{ height: w + 'px', background: currentColor }"></span>
                        </span>
                    </div>
                </div>
            </div>
            <!-- Opacity slider -->
            <div class="dep-row">
                <label class="dep-label">Opacity</label>
                <div class="dep-slider-row">
                    <input type="range" min="0" max="100" step="5"
                        :value="currentOpacity"
                        @input="setOpacity(Number($event.target.value))"
                        @mousedown.stop
                        class="dep-range" />
                    <span class="dep-range-val">{{ currentOpacity }}%</span>
                </div>
            </div>
            <div v-if="/Auto\s*Pattern/i.test(toolName)" class="dep-row">
                <label>Patterns<select aria-label="Pattern family" :value="settings.patternFamily || 'all'" @change="change({patternFamily:$event.target.value})"><option value="all">All</option><option value="candlestick">Candlestick</option><option value="chart">Chart formations</option></select></label>
                <label>Symbol size<select aria-label="Pattern symbol size" :value="settings.patternSymbolSize || 22" @change="change({patternSymbolSize:Number($event.target.value)})"><option :value="16">Small</option><option :value="22">Medium</option><option :value="28">Large</option></select></label>
                <label><input type="checkbox" :checked="settings.compactPatternBadges!==false" @change="change({compactPatternBadges:$event.target.checked})" /> Group nearby symbols</label>
                <label><input type="checkbox" :checked="!!settings.detailedPatternLabels" @change="change({detailedPatternLabels:$event.target.checked})" /> Detailed candle labels</label>
                <label><input type="checkbox" :checked="settings.showPatternOutlines!==false" @change="change({showPatternOutlines:$event.target.checked})" /> Pattern outlines</label>
            </div>
            <!-- Font size for text tools -->
            <div class="dep-row" v-if="isTextTool">
                <label class="dep-label">Font Size</label>
                <div class="dep-widths">
                    <span v-for="fs in fontSizes" :key="fs"
                        class="dep-width-btn dep-fs-btn"
                        :class="{ active: fs === currentFontSize }"
                        @click="setFontSize(fs)">
                        {{ fs }}
                    </span>
                </div>
            </div>
            <div class="dep-row" v-if="hasLine"><label class="dep-label">Line style</label><select aria-label="Drawing line style" :value="settings.lineStyle || 'solid'" @change="change({lineStyle:$event.target.value})"><option>solid</option><option>dashed</option><option>dotted</option></select></div>
            <div class="dep-row" v-if="hasFill"><label><input type="checkbox" :checked="settings.fill!==false" @change="change({fill:$event.target.checked})" /> Fill</label><input type="color" aria-label="Drawing fill color" :value="/^#[0-9a-f]{6}$/i.test(settings.fillColor || '') ? settings.fillColor : '#2962ff'" @mousedown.stop @input="change({fill:true,fillColor:$event.target.value,backColor:$event.target.value})" /> <label><input type="checkbox" :checked="settings.showLabels!==false" @change="change({showLabels:$event.target.checked})" /> Labels</label></div>
            <div class="dep-row" v-if="hasFill"><label class="dep-label">Fill color</label><input aria-label="Drawing fill color" type="color" :value="/^#[0-9a-f]{6}$/i.test(settings.backColor || '')?settings.backColor:'#2962ff'" @input="change({backColor:$event.target.value+'33'})" /></div>
            <div class="dep-row" v-if="canExtend"><label><input type="checkbox" :checked="settings.extendLeft" @change="change({extendLeft:$event.target.checked})" /> Extend left</label> <label><input type="checkbox" :checked="settings.extendRight" @change="change({extendRight:$event.target.checked})" /> Extend right</label></div>
            <div class="dep-row" v-if="/fib|gann|pitch/i.test(toolName)"><label class="dep-label">Levels (comma separated)</label><input class="dep-text-input" aria-label="Drawing levels" :value="(settings.levels || [0,.236,.382,.5,.618,.786,1,1.618,2.618]).map(x=>typeof x==='number'?x:x.value).join(',')" @change="setLevels($event.target.value)" /><div v-for="(level,index) in levelRows" :key="index" class="dep-level-row"><input type="checkbox" :aria-label="'Show level '+level.value" :checked="level.visible!==false" @change="setLevel(index,{visible:$event.target.checked})" /><span>{{level.value}}</span><input type="color" :aria-label="'Color for level '+level.value" :value="level.color || currentColor" @input="setLevel(index,{color:$event.target.value})" /></div><label><input type="checkbox" :checked="settings.reverse" @change="change({reverse:$event.target.checked})" /> Reverse</label> <label><input type="checkbox" :checked="settings.fibLog" @change="change({fibLog:$event.target.checked})" /> Log levels</label></div>
            <div class="dep-row" v-if="/Gann/.test(toolName)"><label><input type="checkbox" :checked="settings.lockRatio" @change="setRatioLock($event.target.checked)" /> Lock price / bar ratio</label></div>
            <div class="dep-row" v-for="field in numberFields" :key="field.key"><label class="dep-label">{{field.label}}</label><input class="dep-text-input" :aria-label="field.label" type="number" :min="field.min" :max="field.max" step="any" :value="settings[field.key] ?? field.value" @change="setNumber(field,$event.target.value)" /></div>
            <div class="dep-row" v-if="/image/i.test(toolName)"><label class="dep-label">Image file</label><input type="file" accept="image/png,image/jpeg,image/webp,image/gif" aria-label="Drawing image" @change="setImage" /></div>
            <div class="dep-row" v-if="/X Post|Idea/.test(toolName)"><label class="dep-label">Link</label><input class="dep-text-input" aria-label="Drawing link" :value="settings.link || ''" @change="change({link:$event.target.value})" /><small>Loads public content from the provider after you click Load.</small><button @click="loadMedia">Load external content</button><p role="status">{{mediaStatus}}</p><iframe v-if="mediaHtml" :srcdoc="mediaHtml" sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox" referrerpolicy="no-referrer" title="Public drawing media" class="drawing-media-frame"></iframe></div>
            <div class="dep-row" v-if="/volume profile/i.test(toolName)"><label>Volume display<select aria-label="Profile volume mode" :value="settings.profileMode || 'total'" @change="change({profileMode:$event.target.value})"><option value="total">Total</option><option value="updown">Up / down candles</option><option value="delta">Candle delta</option></select></label><label>Lower timeframe<input aria-label="Profile lower timeframe" :value="settings.profileTimeframe || '1m'" @change="change({profileTimeframe:$event.target.value})" /></label><button @click="$emit('load-profile',settings.$uuid,settings.profileTimeframe || '1m')">Load intrabars</button><small>{{settings.profileStatus}}</small></div>
            <DrawingCloudPanel v-if="cloudDrawing" :drawing="cloudDrawing" @change="change" @remote="(drawing,meta)=>$emit('remote-drawing',drawing,meta)" />
            <!-- Less-common settings tucked away so the panel stays short by default -->
        </div>
            <details v-show="settingsSection==='Coordinates & visibility'" open class="dep-row dep-more">
                <summary>More options</summary>
                <div class="dep-coordinate" v-for="key in anchorKeys" :key="key"><label>{{key}}</label><input type="number" :aria-label="key+' time'" :value="settings[key][0]" @change="setCoordinate(key,0,$event.target.value)" /><input type="number" step="any" :aria-label="key+' price'" :value="settings[key][1]" @change="setCoordinate(key,1,$event.target.value)" /></div>
                <div class="dep-row"><label class="dep-label">Visible intervals (empty = all)</label><input class="dep-text-input" aria-label="Drawing visible intervals" :value="(settings.visibleIntervals || []).join(',')" placeholder="5m,1H,1D" @change="change({visibleIntervals:$event.target.value.split(',').map(x=>x.trim()).filter(Boolean)})" /></div>
                <div class="dep-row"><label class="dep-label">Synchronize drawing</label><select aria-label="Drawing synchronization" :value="settings.syncScope || 'local'" @change="change({syncScope:$event.target.value})"><option value="local">This chart only</option><option value="workspace">Same instrument · all panes/timeframes</option><option value="global">All instruments · same time/price coordinates</option></select></div>
                <div class="dep-row dep-actions"><button class="dep-btn" @click="change({$locked:!settings.$locked})">{{settings.$locked?'Unlock':'Lock'}}</button><button class="dep-btn" @click="$emit('clone-tool')">Duplicate</button><button class="dep-btn" @click="saveTemplate">{{styleSaved?'Style saved':'Save style'}}</button></div>
            </details>
            <!-- Actions -->
            <div class="dep-row dep-actions">
                <button class="dep-btn" @click="cancelChanges">Cancel</button><button class="dep-btn dep-btn-confirm" @click="close">Done</button>
                <button class="dep-btn dep-btn-delete" @click="deleteTool" title="Delete this drawing">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/>
                        <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                    </svg>
                    <span class="dep-btn-label">Delete</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import DrawingCloudPanel from './DrawingCloudPanel.vue'
import PatternSymbolIcon from './PatternSymbolIcon.vue'
import { PATTERN_CATALOG } from '../workspace/pattern-catalog.mjs'
import {DRAWING_ICONS,DRAWING_EMOJI} from '../workspace/drawing-icons.mjs'
import {botRequest} from '../services/bot_service'
export default {
    name: 'DrawingEditPopup',
    components:{DrawingCloudPanel,PatternSymbolIcon},
    props: {
        cloudDrawing: Object,
        anchorInterval: {type:Number,default:1},
        visible: { type: Boolean, default: false },
        settings: { type: Object, default: () => ({}) },
        toolName: { type: String, default: 'Drawing' },
        posX: { type: Number, default: 200 },
        posY: { type: Number, default: 100 }
    },
    data() {
        return {
            settingsSection:'Style',patternSearch:'',
            mediaHtml:'',mediaStatus:'',
            iconOptions:DRAWING_ICONS,emojiOptions:DRAWING_EMOJI,
            styleSaved:false,
            originalSettings:null,
            offsetX: 0,
            offsetY: 0,
            clampX: 0,
            clampY: 0,
            dragStartX: 0,
            dragStartY: 0,
            dragging: false,
            palette: [
                '#2962ff', '#e91e63', '#ff9800', '#4caf50',
                '#00bcd4', '#9c27b0', '#f44336', '#787b86',
                '#e91a1a', '#23a776', '#e54150', '#ffffff',
                '#1540B3', '#ff5252', '#ffeb3b', '#131722'
            ],
            widths: [1, 2, 3, 4, 5],
            fontSizes: [10, 12, 14, 16, 20, 24]
        }
    },
    computed: {
        patternCatalogSize(){return PATTERN_CATALOG.length},
        patternChoices(){const term=this.patternSearch.trim().toLowerCase();return PATTERN_CATALOG.filter(pattern=>!term||pattern.name.toLowerCase().includes(term)||pattern.kind.includes(term))},
        enabledPatternCount(){return PATTERN_CATALOG.filter(pattern=>this.settings.patternVisibility?.[pattern.id]!==false).length},
        isDark() {
            return document.body.classList.contains('theme-dark') ||
                document.querySelector('.chart-page-dark') !== null
        },
        popupStyle() {
            return {
                left: (this.posX + this.offsetX + this.clampX) + 'px',
                top: (this.posY + this.offsetY + this.clampY) + 'px'
            }
        },
        currentColor() {
            return (this.settings && this.settings.color) || '#2962ff'
        },
        currentWidth() {
            return (this.settings && this.settings.lineWidth) || 1
        },
        currentText() {
            return (this.settings && this.settings.text) || ''
        },
        currentOpacity() {
            if (this.settings && this.settings.opacity !== undefined) {
                return this.settings.opacity
            }
            return 100
        },
        currentFontSize() {
            return (this.settings && this.settings.fontSize) || 14
        },
        levelRows() {return (this.settings.levels || [0,.236,.382,.5,.618,.786,1,1.618,2.618]).map(x=>typeof x==='number'?{value:x}:x)},
        anchorKeys() { return Object.keys(this.settings).filter(k=>/^p\d+$/.test(k)).sort((a,b)=>Number(a.slice(1))-Number(b.slice(1))) },
        numberFields() {
            const n=this.toolName || '',fields=[];
            if(/position/i.test(n))fields.push({key:'rrRatio',label:'Reward / risk',value:3,min:.01,max:100},{key:'accountSize',label:'Account size',value:10000,min:0},{key:'riskPercent',label:'Risk percent',value:1,min:0,max:100});
            if(/position/i.test(n)&&this.settings.p1&&this.settings.p2){const entry=this.settings.p1[1],risk=Math.abs(this.settings.p2[1]-entry);fields.push({key:'target',label:'Target price',value:entry+(/short/i.test(n)?-1:1)*risk*(this.settings.rrRatio ?? 3)})}
            if(/regression/i.test(n))fields.push({key:'deviation',label:'Standard deviations',value:2,min:0,max:10});
            if(/volume profile/i.test(n))fields.push({key:'valueArea',label:'Value area percent',value:70,min:0,max:100});
            if(/volume profile/i.test(n))fields.push({key:'rows',label:'Profile rows',value:24,min:2,max:200});
            if(/gann/i.test(n))fields.push({key:'pricePerBar',label:'Price per bar',value:1,min:.00000001});
            if(/ghost/i.test(n))fields.push({key:'mintick',label:'Minimum price tick',value:.01,min:.000000001},{key:'ghostTicks',label:'Average ghost range in ticks',value:100,min:1},{key:'varianceTicks',label:'Ghost variance in ticks',value:50,min:0});
            if(/ghost/i.test(n))fields.push({key:'candleCount',label:'Ghost candle count',value:20,min:2,max:200},{key:'ghostRange',label:'Ghost candle price range',value:1,min:0},{key:'variance',label:'Ghost variance',value:.5,min:0,max:10});
            return fields;
        },
        isTextTool() {
            let n = (this.toolName || '').toLowerCase()
            return n === 'text' || n === 'label' || n === 'callout'
                || n === 'textnote'
                || /note|callout|comment|signpost|pin|flag|table|icon|emoji|sticker|arrow mark|X Post|Idea/i.test(this.toolName)
                || (this.settings && this.settings.text !== undefined)
        },
        // Icons/emoji/stickers and pure text annotations have no line or fill of their own.
        isPointTool() {
            return /icon|emoji|sticker/i.test(this.toolName) || this.isTextTool
        },
        // Open (two-point) line tools draw a stroke but never a fillable area.
        isLineTool() {
            return !this.isPointTool
                && !/channel|pitchfork|box/i.test(this.toolName)
                && /line|ray|angle|regression/i.test(this.toolName)
        },
        hasLine() { return !this.isPointTool },
        hasFill() { return !this.isPointTool && !this.isLineTool },
        canExtend() { return this.isLineTool }
    },
    watch: {
        toolName() {this.settingsSection='Style';this.patternSearch=''},
        'settings.$uuid'() {this.mediaHtml='';this.mediaStatus='';this.originalSettings=JSON.parse(JSON.stringify(this.settings));this.styleSaved=false;this.$nextTick(this.recalcClamp)},
        visible(v) {
            if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null }
            if (v) {
                this.originalSettings=JSON.parse(JSON.stringify(this.settings));this.styleSaved=false
                this.offsetX = 0
                this.offsetY = 0
                this.clampX = 0
                this.clampY = 0
                this.$nextTick(() => {
                    this.recalcClamp()
                    if (typeof ResizeObserver !== 'undefined' && this.$el && this.$el.nodeType === 1) {
                        this.resizeObserver = new ResizeObserver(this.recalcClamp)
                        this.resizeObserver.observe(this.$el)
                    }
                })
            }
        },
        posX() { this.$nextTick(this.recalcClamp) },
        posY() { this.$nextTick(this.recalcClamp) }
    },
    mounted() {
        document.addEventListener('mousemove', this.onDrag)
        document.addEventListener('mouseup', this.stopDrag)
        window.addEventListener('resize', this.recalcClamp)
    },
    beforeDestroy() {
        document.removeEventListener('mousemove', this.onDrag)
        document.removeEventListener('mouseup', this.stopDrag)
        window.removeEventListener('resize', this.recalcClamp)
        if (this.resizeObserver) this.resizeObserver.disconnect()
    },
    methods: {
        // Keeps the popup fully on-screen no matter how tall its content gets
        // (tool-specific sections, the "More options" accordion opening, etc).
        recalcClamp() {
            if (!this.visible || !this.$el) return
            const rect = this.$el.getBoundingClientRect()
            const margin = 8
            let dx = 0, dy = 0
            if (rect.right > window.innerWidth - margin) dx = (window.innerWidth - margin) - rect.right
            if (rect.left + dx < margin) dx = margin - rect.left
            if (rect.bottom > window.innerHeight - margin) dy = (window.innerHeight - margin) - rect.bottom
            if (rect.top + dy < margin) dy = margin - rect.top
            this.clampX = dx
            this.clampY = dy
        },
        cancelChanges() {if(this.originalSettings){const changes={...this.originalSettings};for(const key of Object.keys(this.settings))if(!(key in changes))changes[key]=undefined;this.change(changes)}this.close()},
        async loadMedia() {this.mediaStatus='Loading public content…';this.mediaHtml='';try{const result=await botRequest('/chart/drawing-media?url='+encodeURIComponent(this.settings.link || ''));this.mediaHtml=result.html;this.mediaStatus=result.title;this.change({text:[result.title,result.description].filter(Boolean).join('\n')})}catch(error){this.mediaStatus=error.message}},
        togglePattern(id,enabled){this.change({patternVisibility:{...this.settings.patternVisibility,[id]:enabled}})},
        selectShownPatterns(enabled){const visibility={...this.settings.patternVisibility};for(const pattern of this.patternChoices)visibility[pattern.id]=enabled;this.change({patternVisibility:visibility})},
        selectPatterns(enabled){this.change({patternVisibility:Object.fromEntries(PATTERN_CATALOG.map(pattern=>[pattern.id,enabled]))})},
        change(value) { this.$emit('change-settings',value) },
        setLevel(index,changes) {const levels=this.levelRows.map(l=>({...l}));levels[index]={...levels[index],...changes};this.change({levels})},
        setLevels(value) {const levels=value.split(',').map(x=>Number(x.trim()));if(levels.length && levels.every(Number.isFinite))this.change({levels:levels.slice(0,32)})},
        setNumber(field,value) {const n=Number(value);if(Number.isFinite(n))this.change({[field.key]:Math.min(field.max ?? Infinity,Math.max(field.min ?? -Infinity,n))})},
        setRatioLock(enabled) {const a=this.settings.p1,b=this.settings.p2,bars=a&&b?(b[0]-a[0])/this.anchorInterval:0;this.change({lockRatio:enabled,...(enabled&&bars?{pricePerBar:(b[1]-a[1])/bars}:{})})},
        setCoordinate(key,index,value) {const n=Number(value);if(!Number.isFinite(n))return;const p=this.settings[key].slice();p[index]=n;this.change({[key]:p})},
        setImage(event) {const file=event.target.files[0];if(!file || file.size>2*1024*1024 || !/^image\/(png|jpeg|webp|gif)$/.test(file.type))return;const reader=new FileReader();reader.onload=()=>this.change({image:reader.result});reader.readAsDataURL(file)},
        saveTemplate() {const style={};for(const key of ['color','backColor','lineWidth','lineStyle','opacity','fill','fontSize','showLabels','patternFamily','patternSymbolSize','showPatternOutlines','patternVisibility','compactPatternBadges','detailedPatternLabels','requirePatternTrend','patternPivotStrength','levels','rrRatio','rows','deviation'])if(this.settings[key]!==undefined)style[key]=this.settings[key];try{localStorage.setItem('tc_drawing_template:'+this.toolName,JSON.stringify(style));this.styleSaved=true}catch(_){}},
        swatchStyle(c) {
            let style = { background: c }
            // Add a border for white/light swatches so they're visible
            if (c === '#ffffff' || c === '#fff') {
                style.border = '1px solid #d1d4dc'
            }
            return style
        },
        startDrag(e) {
            if (!e.target.closest('.dep-header')) return
            this.dragging = true
            this.dragStartX = e.clientX - this.offsetX
            this.dragStartY = e.clientY - this.offsetY
        },
        onDrag(e) {
            if (!this.dragging) return
            this.offsetX = e.clientX - this.dragStartX
            this.offsetY = e.clientY - this.dragStartY
        },
        stopDrag() {
            this.dragging = false
        },
        setColor(c) {
            this.$emit('change-settings', { color: c })
        },
        setWidth(w) {
            this.$emit('change-settings', { lineWidth: w })
        },
        setText(t) {
            this.$emit('change-settings', { text: t })
        },
        setOpacity(v) {
            this.$emit('change-settings', { opacity: v })
        },
        setFontSize(fs) {
            this.$emit('change-settings', { fontSize: fs })
        },
        deleteTool() {
            this.$emit('delete-tool')
        },
        close() {
            this.$emit('close')
        }
    }
}
</script>

<style>
.drawing-edit-popup {
    position: fixed;
    z-index: 10000;
    background: #ffffff;
    border: 1px solid #e0e3eb;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08);
    min-width: 230px;
    max-width: 300px;
    max-height: calc(100vh - 30px);
    overflow: auto;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 12px;
    user-select: none;
}
/* Dark theme */
.drawing-edit-popup.dep-dark {
    background: #1a2035;
    border-color: rgba(55, 59, 75, 0.6);
    box-shadow: 0 4px 20px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.3);
}
.dep-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid #f0f3fa;
    cursor: move;
}
.dep-dark .dep-header {
    border-bottom-color: rgba(55, 59, 75, 0.4);
}
.dep-title {
    font-weight: 600;
    color: #131722;
    font-size: 12px;
    text-transform: capitalize;
}
.dep-dark .dep-title {
    color: #d1d4dc;
}
.dep-close {
    background: none;
    border: none;
    font-size: 18px;
    color: #787b86;
    cursor: pointer;
    padding: 0 2px;
    line-height: 1;
}
.dep-close:hover { color: #131722; }
.dep-dark .dep-close:hover { color: #d1d4dc; }
.dep-body { padding: 10px 12px; }
.dep-row { margin-bottom: 10px; }
.dep-row:last-child { margin-bottom: 0; }
.dep-label {
    display: block;
    color: #787b86;
    font-size: 10px;
    margin-bottom: 5px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.dep-text-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    font-size: 12px;
    font-family: inherit;
    color: #131722;
    outline: none;
    box-sizing: border-box;
}
.dep-dark .dep-text-input {
    background: #1e2436;
    border-color: rgba(55, 59, 75, 0.6);
    color: #d1d4dc;
}
.dep-text-input:focus {
    border-color: #2962ff;
}
.dep-style-row {
    display: flex;
    gap: 14px;
}
.dep-style-col {
    flex: 1;
    min-width: 0;
}
.dep-style-col-color {
    flex: 1.4;
}
.dep-colors {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
}
.dep-swatch {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: border-color 0.1s, transform 0.1s;
    box-sizing: border-box;
}
.dep-swatch:hover { border-color: #b2b5be; transform: scale(1.1); }
.dep-swatch.active { border-color: #2962ff; transform: scale(1.1); }
.dep-dark .dep-swatch:hover { border-color: #5b9cf6; }
.dep-dark .dep-swatch.active { border-color: #5b9cf6; }

.dep-custom-color {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
    border-radius: 4px;
}
.dep-color-input {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}
.dep-custom-icon {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    pointer-events: none;
}

.dep-widths {
    display: flex;
    gap: 4px;
}
.dep-width-btn {
    width: 28px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid transparent;
    background: #f8f9fd;
    transition: all 0.1s;
}
.dep-dark .dep-width-btn {
    background: #1e2436;
}
.dep-width-btn:hover { border-color: #b2b5be; }
.dep-width-btn.active {
    border-color: #2962ff;
    background: rgba(41,98,255,0.08);
}
.dep-dark .dep-width-btn:hover { border-color: #5b9cf6; }
.dep-dark .dep-width-btn.active {
    border-color: #5b9cf6;
    background: rgba(91,156,246,0.15);
}
.dep-width-line {
    width: 18px;
    background: #131722;
    border-radius: 1px;
    display: block;
}
.dep-dark .dep-width-line {
    background: #d1d4dc;
}

.dep-fs-btn {
    font-size: 10px;
    font-weight: 600;
    color: #787b86;
    width: 32px;
}
.dep-fs-btn.active {
    color: #2962ff;
}
.dep-dark .dep-fs-btn.active {
    color: #5b9cf6;
}

.dep-slider-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.dep-range {
    flex: 1;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: #e0e3eb;
    border-radius: 2px;
    outline: none;
}
.dep-dark .dep-range {
    background: #2f3240;
}
.dep-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #2962ff;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.dep-dark .dep-range::-webkit-slider-thumb {
    background: #5b9cf6;
    border-color: #1a2035;
}
.dep-range-val {
    font-size: 10px;
    color: #787b86;
    min-width: 32px;
    text-align: right;
}

.dep-actions {
    display: flex;
    gap: 6px;
    padding-top: 8px;
    border-top: 1px solid #f0f3fa;
    margin-top: 6px;
}
.dep-dark .dep-actions {
    border-top-color: rgba(55, 59, 75, 0.4);
}
.dep-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 28px;
    padding: 0 10px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    background: #fff;
    color: #787b86;
    cursor: pointer;
    transition: all 0.15s;
    font-size: 11px;
}
.dep-dark .dep-btn {
    background: #1e2436;
    border-color: rgba(55, 59, 75, 0.6);
    color: #787b86;
}
.dep-btn:hover { border-color: #b2b5be; color: #131722; }
.dep-dark .dep-btn:hover { border-color: #5b9cf6; color: #d1d4dc; }
.dep-btn-delete:hover { color: #e54150; border-color: #e54150; }
.dep-dark .dep-btn-delete:hover { color: #ef5350; border-color: #ef5350; }
.dep-btn-label {
    font-weight: 500;
}
</style>

<style>
.dep-coordinate{display:grid;grid-template-columns:20px 1fr 1fr;gap:4px;margin:5px 0}.dep-coordinate input{min-width:0;width:100%;box-sizing:border-box}.dep-body select,.dep-body input[type=number]{background:transparent;color:inherit;border:1px solid #8885;border-radius:3px;padding:4px}.dep-dark .dep-body{color:#d1d4dc}.dep-body summary{cursor:pointer}
.dep-more{border-top:1px solid #f0f3fa;padding-top:8px}.dep-dark .dep-more{border-top-color:rgba(55,59,75,0.4)}
.dep-more summary{font-size:11px;font-weight:600;color:#787b86;text-transform:uppercase;letter-spacing:0.04em;list-style:none}
.dep-more summary::-webkit-details-marker{display:none}
.dep-more summary::before{content:'▸ ';display:inline-block;transition:transform 0.1s}
.dep-more[open] summary::before{transform:rotate(90deg)}
.dep-more > *:not(summary){margin-top:10px}
</style>
<style>.dep-level-row{display:flex;align-items:center;gap:8px;margin:4px 0}.dep-level-row span{flex:1}.dep-level-row input[type=color]{width:28px;height:20px;border:0;padding:0}</style>
<style>.drawing-media-frame{width:100%;height:350px;border:1px solid #8885;border-radius:4px;background:white}</style>
<style>.drawing-symbol-picker{display:flex;gap:4px;flex-wrap:wrap}.drawing-symbol-picker button{padding:3px;border:1px solid #8885;background:transparent;border-radius:3px;cursor:pointer;font-size:22px}</style>
<style>
.dep-settings-dialog { width:min(620px,calc(100vw - 24px))!important; max-height:calc(100dvh - 24px); border-radius:14px; box-shadow:0 16px 60px #0003; overflow:hidden; }
.dep-settings-dialog .dep-header { padding:18px 20px; cursor:move; }
.dep-settings-dialog .dep-title { font-size:18px; font-weight:600; }
.dep-settings-tabs { display:flex; gap:8px; padding:0 16px 12px; border-bottom:1px solid #e5e7eb; }
.dep-settings-tabs button { padding:9px 12px; border:0; border-radius:7px; background:transparent; color:inherit; cursor:pointer; }
.dep-settings-tabs button.active { background:#2962ff12; color:#2962ff; }
.dep-settings-dialog .dep-body { padding:20px; max-height:calc(100dvh - 150px); overflow:auto; }
.dep-settings-dialog .dep-row { margin-bottom:18px; }
.dep-settings-dialog.dep-dark .dep-settings-tabs { border-color:#363c48; }
</style>

<style scoped>
.dep-pattern-list{max-height:240px;overflow:auto;border:1px solid #787b8640;border-radius:4px;padding:4px}.dep-pattern-choice{display:flex;align-items:center;gap:7px;min-height:32px;font-size:12px;cursor:pointer}.dep-pattern-choice svg{flex-shrink:0}.dep-pattern-choice input{flex-shrink:0}
</style>
